/**
 * traySegregation.logic.ts
 * Pure logic for cable tray pathways, capacity calculation, and media segregation rules
 * according to TIA-942, TIA-569, and BICSI 002.
 */

export type TrayMedia = 'fiber' | 'copper' | 'power';
export type TrayConstruction = 'fiber_raceway' | 'wire_mesh' | 'power_busway' | 'ladder' | 'underfloor';

export interface TrayMediaConfig {
  media: TrayMedia;
  label: string;
  shortLabel: string;
  color: string;
  defaultType: TrayConstruction;
  dash?: number[];
  strokeWidthFactor: number;
}

export interface TrayFillStatus {
  ratio: number;
  percent: number;
  status: 'normal' | 'warning' | 'overfill';
  color: string;
}

export interface SegregationValidationResult {
  isValid: boolean;
  violations: string[];
}

export interface ProximityCheckResult {
  hasViolation: boolean;
  message?: string;
  distanceM?: number;
}

// ─── 1. Tray Media Configuration ──────────────────────────────────────────────
export function getTrayMediaConfig(media?: string): TrayMediaConfig {
  const norm = (media || '').toLowerCase();

  if (norm === 'fiber' || norm === 'fiber_raceway') {
    return {
      media: 'fiber',
      label: 'Оптический короб (Fiber Raceway)',
      shortLabel: 'Оптика',
      color: '#f59e0b', // Safety Yellow / Amber
      defaultType: 'fiber_raceway',
      dash: undefined,
      strokeWidthFactor: 1.0,
    };
  }

  if (norm === 'power' || norm === 'power_busway' || norm === 'ladder') {
    return {
      media: 'power',
      label: 'Шинный мост / Силовой лоток (Power Busway)',
      shortLabel: 'Сила',
      color: '#ef4444', // High-voltage Red
      defaultType: 'power_busway',
      dash: [12, 6],
      strokeWidthFactor: 1.25,
    };
  }

  // Default: copper / wire_mesh
  return {
    media: 'copper',
    label: 'Проволочный лоток СКС (Wire Mesh)',
    shortLabel: 'Медь',
    color: '#0ea5e9', // Sky Blue / Zinc Mesh
    defaultType: 'wire_mesh',
    dash: [6, 4],
    strokeWidthFactor: 1.0,
  };
}

// ─── 2. TIA-569 40% Standard Cable Capacity ──────────────────────────────────
export function calculateTrayMaxCables(widthM?: number, mediaType: string = 'copper'): number {
  const w = widthM != null && widthM > 0 ? Math.max(0.1, widthM) : 0.1;
  const norm = (mediaType || '').toLowerCase();

  if (norm === 'fiber' || norm === 'fiber_raceway') {
    // Standard 2-fiber zipcord / micro-cable (~3.0 mm)
    // 100mm = 120, 200mm = 240, 300mm = 360
    return Math.round(w * 1200);
  }

  if (norm === 'power' || norm === 'power_busway') {
    // Heavy 3-phase feeder cables (~18-22 mm)
    // 100mm = 12, 200mm = 24, 300mm = 36
    return Math.round(w * 120);
  }

  // Copper: Cat6A / Cat7 (~7.5 mm)
  // 100mm = 48, 200mm = 96, 300mm = 144, 400mm = 192, 600mm = 288
  return Math.round(w * 480);
}

// ─── 3. Fill Ratio & Threshold Status ─────────────────────────────────────────
export function calculateTrayFillStatus(usedCables: number, maxCables: number): TrayFillStatus {
  const used = Math.max(0, usedCables || 0);
  const max = Math.max(1, maxCables || 1);
  const ratio = used / max;
  const percent = Math.round(ratio * 100);

  if (ratio < 0.6) {
    return { ratio, percent, status: 'normal', color: '#22c55e' };
  }
  if (ratio <= 0.85) {
    return { ratio, percent, status: 'warning', color: '#eab308' };
  }
  return { ratio, percent, status: 'overfill', color: '#ef4444' };
}

// ─── 4. Media Segregation Validation ──────────────────────────────────────────
export function validateMediaSegregation(
  edge: { id?: string; type?: string; [k: string]: any },
  routedCables: Array<{ cableType?: string; isPower?: boolean }>,
): SegregationValidationResult {
  const edgeMedia = getTrayMediaConfig(edge.type).media;
  const violations: string[] = [];

  for (const cable of routedCables) {
    const cableType = (cable.cableType || '').toLowerCase();
    const isPower = Boolean(cable.isPower || cableType.includes('power') || cableType.includes('feed'));
    const isFiber = cableType.includes('fiber') || cableType.includes('opt');
    const isCopper = !isFiber && !isPower;

    if (edgeMedia === 'fiber' && (isCopper || isPower)) {
      violations.push('В оптическом лотке запрещена прокладка меди или силовых кабелей (нарушение TIA-942)');
      break;
    }

    if (edgeMedia === 'power' && (isCopper || isFiber)) {
      violations.push('ЭМП-риск: прокладка слаботочных кабелей в силовом шинопроводе без экранирующего барьера');
      break;
    }

    if (edgeMedia === 'copper' && isPower) {
      violations.push('Нарушение TIA-942: силовые проводники в слаботочном лотке вызывают электромагнитные наводки');
      break;
    }
  }

  return {
    isValid: violations.length === 0,
    violations,
  };
}

// ─── 5. Proximity Clearance Validation ────────────────────────────────────────
interface Point2D {
  x: number;
  y: number;
}

interface SegmentEdge {
  id?: string;
  type?: string;
  sourcePos: Point2D;
  targetPos: Point2D;
}

export function distToSegmentSquared(p: Point2D, v: Point2D, w: Point2D): number {
  const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
  if (l2 === 0) return (p.x - v.x) ** 2 + (p.y - v.y) ** 2;
  let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return (p.x - (v.x + t * (w.x - v.x))) ** 2 + (p.y - (v.y + t * (w.y - v.y))) ** 2;
}

function segmentDistance(a1: Point2D, a2: Point2D, b1: Point2D, b2: Point2D): number {
  const d1 = distToSegmentSquared(a1, b1, b2);
  const d2 = distToSegmentSquared(a2, b1, b2);
  const d3 = distToSegmentSquared(b1, a1, a2);
  const d4 = distToSegmentSquared(b2, a1, a2);
  return Math.sqrt(Math.min(d1, d2, d3, d4));
}

export function checkProximitySegregation(
  edgeA: SegmentEdge,
  edgeB: SegmentEdge,
  minClearanceM: number = 0.3,
): ProximityCheckResult {
  const mediaA = getTrayMediaConfig(edgeA.type).media;
  const mediaB = getTrayMediaConfig(edgeB.type).media;

  // Segregation requirement only applies between Power and Data (copper/fiber)
  const isPowerA = mediaA === 'power';
  const isPowerB = mediaB === 'power';

  if ((isPowerA && !isPowerB) || (!isPowerA && isPowerB)) {
    const minDistancePx = segmentDistance(edgeA.sourcePos, edgeA.targetPos, edgeB.sourcePos, edgeB.targetPos);
    const distanceM = +(minDistancePx / 100).toFixed(3); // PPM = 100 px/m

    if (distanceM < minClearanceM) {
      return {
        hasViolation: true,
        message: `Недостаточный зазор между силовой и информационной трассой (< 300 мм: ${Math.round(distanceM * 1000)} мм)`,
        distanceM,
      };
    }
  }

  return { hasViolation: false };
}
